import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const empty = { name: "", kind: "both", color: "#0F172A", account_head: "" };

const COMMON_HEADS = [
  "Revenue",
  "Other Income",
  "Cost of Goods Sold",
  "Operating Expense",
  "Salary & Wages",
  "Rent & Utilities",
  "Capital Expenditure",
  "Loans & Financing",
  "Taxes & Statutory",
  "Owner Draw",
];

export default function Categories() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const fetchAll = async () => {
    const r = await api.get("/categories");
    setItems(r.data);
  };

  useEffect(() => { fetchAll(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      name: it.name,
      kind: it.kind,
      color: it.color || "#0F172A",
      account_head: it.account_head || "",
    });
    setOpen(true);
  };

  // Build the suggestion list: common heads + any custom heads already in use across the workspace
  const headSuggestions = Array.from(new Set([
    ...COMMON_HEADS,
    ...items.map((c) => (c.account_head || "").trim()).filter(Boolean),
  ])).sort();

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/categories/${editing.id}`, form);
        toast.success("Category updated");
      } else {
        await api.post("/categories", form);
        toast.success("Category created");
      }
      setOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    await api.delete(`/categories/${id}`);
    toast.success("Category deleted");
    fetchAll();
  };

  return (
    <div className="p-10 max-w-4xl" data-testid="categories-page">
      <PageHeader overline="Taxonomy" title="Categories" testid="categories-header">
        {isAdmin && (
          <Button onClick={openNew} data-testid="add-category-button"
            className="rounded-none bg-[#0F172A] hover:bg-[#1E293B] text-white h-10 text-xs uppercase tracking-wider font-semibold">
            + New Category
          </Button>
        )}
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-[10px]" data-testid="categories-list">
        {items.length === 0 && (
          <div className="col-span-2 bg-white px-6 py-16 text-center text-sm text-[#6B7280]" data-testid="categories-empty">
            No categories yet.
          </div>
        )}
        {items.map((c) => (
          <div key={c.id} className="bg-white border border-[#E3E3E3] p-5 flex items-center justify-between" data-testid={`category-card-${c.id}`}>
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-1 h-10 shrink-0" style={{ background: c.color }} />
              <div className="min-w-0">
                <div className="font-medium text-[#111827] truncate">{c.name}</div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="overline">{c.kind}</span>
                  {c.account_head && (
                    <span
                      className="text-[10px] uppercase tracking-wider bg-[#F3F4F6] text-[#4B5563] px-1.5 py-0.5 border border-[#E5E7EB]"
                      data-testid={`category-head-${c.id}`}
                      title="Account Head"
                    >
                      {c.account_head}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {isAdmin ? (
                <>
                  <button onClick={() => openEdit(c)} data-testid={`edit-category-${c.id}`}
                    className="p-1.5 hover:bg-[#E5E7EB]"><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={() => onDelete(c.id)} data-testid={`delete-category-${c.id}`}
                    className="p-1.5 hover:bg-[#EF4444]/10 text-[#EF4444]"><Trash2 className="h-3.5 w-3.5" /></button>
                </>
              ) : (
                <span className="text-xs text-[#9CA3AF]">—</span>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-md" data-testid="category-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">{editing ? "Edit Category" : "New Category"}</DialogTitle>
            <DialogDescription className="text-xs text-[#6B7280]">
              {editing ? "Update this category's name, kind, or accent color." : "Define a new category to classify your ledger entries (e.g., Sales, Rent, Utilities)."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4 mt-2" data-testid="category-form">
            <div>
              <Label className="overline mb-1.5 block">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required
                data-testid="category-name" className="rounded-none h-10" />
            </div>
            <div>
              <Label className="overline mb-1.5 block">Kind</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="category-kind" className="rounded-none h-10"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="credit">Credit only</SelectItem>
                  <SelectItem value="debit">Debit only</SelectItem>
                  <SelectItem value="both">Both</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline mb-1.5 block">Account Head</Label>
              <Input
                value={form.account_head}
                onChange={(e) => setForm({ ...form, account_head: e.target.value })}
                data-testid="category-account-head"
                list="account-head-suggestions"
                placeholder="e.g., Revenue, Operating Expense"
                className="rounded-none h-10"
              />
              <datalist id="account-head-suggestions">
                {headSuggestions.map((h) => <option key={h} value={h} />)}
              </datalist>
              <p className="text-[10px] text-[#6B7280] mt-1">
                Groups categories for the Reports → Account Heads chart. Pick from the suggestions or type your own.
              </p>
            </div>
            <div>
              <Label className="overline mb-1.5 block">Color</Label>
              <div className="flex items-center gap-3">
                <Input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })}
                  data-testid="category-color" className="rounded-none h-10 w-20 p-1" />
                <Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })}
                  className="rounded-none h-10 font-mono" />
              </div>
            </div>
            <DialogFooter className="pt-3">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="category-cancel"
                className="rounded-none h-10 text-xs uppercase tracking-wider">Cancel</Button>
              <Button type="submit" data-testid="category-save"
                className="rounded-none h-10 bg-[#0F172A] hover:bg-[#1E293B] text-xs uppercase tracking-wider">
                {editing ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
